import {isNil, memoize} from "lodash";
import React, {
    useCallback,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import {useTranslation} from "react-i18next";
import {
    Area,
    AreaChart,
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    LabelList,
    Legend,
    Pie,
    PieChart,
    PolarAngleAxis,
    PolarGrid,
    PolarRadiusAxis,
    Radar,
    RadarChart,
    ReferenceArea,
    ReferenceLine,
    ResponsiveContainer,
    Scatter,
    ScatterChart,
    Sector,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import {getColorByName} from "../../utils/ColorUtils";
import {calculateLegendResult} from "../../utils/DashboardUtils";
import CustomLegend from "../basics/chart/CustomLegend";
import InfoBox from "../basics/info-box/InfoBox";
import {parseLabelList} from "../modals-new/sql-creator-modal/SQLCreatorModalParametersUtils";
import "./_sql-chart.scss";

class ErrorBoundary extends React.Component {
    state = {
        error: null,
    };

    static getDerivedStateFromError(error) {
        return {error};
    }

    componentDidCatch(error, errorInfo) {
        console.error(error, errorInfo);
    }

    render() {
        if (this.state.error) {
            return (
                <InfoBox boxColor={"error"}>
                    <div>{this.props.t("errorWhileRenderingChart")}</div>
                    <div style={{whiteSpace: "pre"}}>
                        {this.state.error.stack}
                    </div>
                </InfoBox>
            );
        }

        return this.props.children;
    }
}

function useLegendClick() {
    const [disabled, setDisabled] = useState([]);

    const onClick = useCallback(({payload}) => {
        if (!payload) return;
        setDisabled((prev) => {
            if (prev.includes(payload.dataKey)) {
                return prev.filter((item) => item !== payload.dataKey);
            }
            return [...prev, payload.dataKey];
        });
    }, []);

    return [disabled, onClick];
}

function usePrintHeight(print, data, formatters) {
    return useMemo(() => {
        if (!print) return {height: "100%"};
        const axisLabels = data.map(({name}) => {
            let text = formatters.x?.formatter
                ? formatters.x.formatter(name)
                : name;
            if (typeof text === "number") text = text + "";
            return text?.length || 0;
        });
        const longest = Math.max(...axisLabels) * 4.5;
        return {height: 300 + longest, xAxisHeight: longest};
    }, [print, data, formatters]);
}

function referenceAreasRenderer(referenceLines) {
    return referenceLines.map((row, index) => {
        if (isNil(row.Value)) return null;
        if (row.Fill === "none") {
            return (
                <ReferenceLine
                    key={index}
                    stroke={row.Color}
                    style={{
                        stroke: row.Color,
                    }}
                    y={row.Value}
                    strokeWidth={
                        row.Thickness === "thick"
                            ? 4
                            : row.Thickness === "thin"
                              ? 1
                              : 2
                    }
                    label={{
                        position: "insideBottomLeft",
                        value: row.Name ? `${row.Name}: ${row.Value}` : null,
                        fill: row.Color,
                    }}
                    isFront
                    ifOverflow="extendDomain"
                    yAxisId={row.Axis}
                />
            );
        }
        if (row.Fill === "between") {
            if (isNil(row.ValueTo)) return null;
            let y1 = row.Value;
            let y2 = row.ValueTo;
            if (y1 > y2) {
                [y1, y2] = [y2, y1];
            }
            return (
                <ReferenceArea
                    key={index}
                    stroke={row.Color}
                    style={{
                        stroke: row.Color,
                    }}
                    fill={row.Color}
                    fillOpacity={0.4}
                    y1={y1}
                    y2={y2}
                    strokeWidth={
                        row.Thickness === "thick"
                            ? 4
                            : row.Thickness === "thin"
                              ? 1
                              : 2
                    }
                    label={{
                        position: "insideBottomLeft",
                        value: row.Name ? `${row.Name}: ${y1} - ${y2}` : null,
                        fill: row.Color,
                    }}
                    isFront
                    ifOverflow="extendDomain"
                    yAxisId={row.Axis}
                />
            );
        }
        return (
            <ReferenceArea
                key={index}
                stroke={row.Color}
                fill={row.Color}
                fillOpacity={0.4}
                y1={row.Fill === "above" ? row.Value : undefined}
                y2={row.Fill === "below" ? row.Value : undefined}
                strokeWidth={
                    row.Thickness === "thick"
                        ? 4
                        : row.Thickness === "thin"
                          ? 1
                          : 2
                }
                label={{
                    position:
                        row.Fill === "below"
                            ? "insideTopLeft"
                            : "insideBottomLeft",
                    value: row.Name ? `${row.Name}: ${row.Value}` : null,
                    fill: row.Color,
                }}
                isFront
                ifOverflow="extendDomain"
                yAxisId={row.Axis}
            />
        );
    });
}

function renderLegend(onClick, legend = "top") {
    if (legend === "none") return null;
    if (legend === "top" || legend === "bottom") {
        return (
            <Legend
                verticalAlign={legend}
                height={36}
                content={CustomLegend}
                onClick={onClick}
            />
        );
    }
    return (
        <Legend
            width={75}
            content={CustomLegend}
            onClick={onClick}
            align={legend}
            verticalAlign="middle"
            layout="vertical"
        />
    );
}

function renderCartesianGrid(print, vertical = false, horizontal = true) {
    if (!print) return null;
    return (
        <CartesianGrid
            strokeDasharray="3 3"
            vertical={vertical}
            horizontal={horizontal}
        />
    );
}

function createName(field, sum, translatedGroupNames, translatedIDs) {
    const name = translatedIDs[field];
    const split = field.split("#");
    if (sum !== undefined) {
        if (split.length === 1) return `${name} (${sum})`;
        return `[${translatedGroupNames[split[1]]}] ${name} (${sum})`;
    }
    if (split.length === 1) return `${name}`;
    return `[${translatedGroupNames[split[1]]}] ${name}`;
}

function CustomTooltipContainer({children, xAxisName, name}) {
    return (
        <div
            className="recharts-default-tooltip"
            style={{margin: 0, padding: 10}}>
            {name && xAxisName && (
                <p className="recharts-tooltip-label">
                    {xAxisName}: {name}
                </p>
            )}
            {children}
        </div>
    );
}

function CustomTooltipItem({
    separator,
    formatter,
    item,
    color = item.color,
    name = item.name,
}) {
    return (
        <li
            className="recharts-tooltip-item"
            style={{
                display: "block",
                paddingTop: 4,
                paddingBottom: 4,
                color,
            }}>
            <span className="recharts-tooltip-item-name">{name}</span>
            <span className="recharts-tooltip-item-separator">{separator}</span>
            <span className="recharts-tooltip-item-value">
                {formatter ? formatter(item.value, name, item) : item.value}
            </span>
            <span className="recharts-tooltip-item-unit">{item.unit}</span>
        </li>
    );
}

function CustomTooltip({
    showOnlyLabelsWithValue,
    label,
    labelFormatter,
    payload,
    separator,
    formatter,
    xAxisName,
    ...props
}) {
    return (
        <CustomTooltipContainer
            name={labelFormatter ? labelFormatter(label) : label}
            xAxisName={xAxisName}>
            <ul className="recharts-tooltip-item-list">
                {payload &&
                    payload.map((item, index) => {
                        if (showOnlyLabelsWithValue && !item.value) return null;
                        return (
                            <CustomTooltipItem
                                key={index}
                                item={item}
                                formatter={formatter}
                                separator={separator}
                            />
                        );
                    })}
            </ul>
        </CustomTooltipContainer>
    );
}

function CustomScatterTooltip({
    label,
    labelFormatter,
    payload,
    chartColors,
    separator,
    formatter,
    xAxisName,
    names,
    ...props
}) {
    const xAxis = useMemo(() => {
        if (!payload) return null;
        return payload.find((item) => item.dataKey === "x");
    }, [payload]);

    const payloadWithoutXAxis = useMemo(() => {
        if (!payload) return [];
        return payload.filter((item) => item.dataKey !== "x");
    }, [payload]);

    return (
        <CustomTooltipContainer xAxisName={xAxisName} name={xAxis?.value}>
            <ul className="recharts-tooltip-item-list">
                {payload &&
                    payloadWithoutXAxis.map((item, index) => (
                        <CustomTooltipItem
                            key={index}
                            formatter={formatter}
                            item={item}
                            separator={separator}
                            color={chartColors[item.payload.field]}
                            name={names[item.payload.field]}
                        />
                    ))}
            </ul>
        </CustomTooltipContainer>
    );
}

function CustomPieChartTooltip({separator, formatter, payload}) {
    return (
        <CustomTooltipContainer>
            <ul className="recharts-tooltip-item-list">
                {payload &&
                    payload.map((item, index) => (
                        <CustomTooltipItem
                            key={index}
                            item={item}
                            formatter={formatter}
                            separator={separator}
                            color={item.payload.fill}
                        />
                    ))}
            </ul>
        </CustomTooltipContainer>
    );
}

function useChartWidth(container) {
    const [size, setSize] = useState("100%");

    useLayoutEffect(() => {
        if (container.current) {
            const rect = container.current.getBoundingClientRect();
            if (rect.width < 500) setSize(800);
        }
    }, [container]);

    return size;
}

function SQLAreaChart({
    data,
    fields,
    chartColors,
    label,
    referenceLines,
    secondAxisParameters,
    formatters,
    onClick,
    highlight,
    legend,
    showAxisName,
    leftDomain,
    rightDomain,
    print,
    sum,
    labelList,
    translatedGroupNames,
    translatedIDs,
    showOnlyLabelsWithValue,
}) {
    const memoizedFormatters = useRef({});

    const [disabled, onLegendClick] = useLegendClick();

    const tooltipFormatter = useCallback(
        (value, _, props) => {
            try {
                if (secondAxisParameters.includes(props.dataKey))
                    return formatters.y2?.formatter(value);
                return formatters.y1?.formatter(value);
            } catch (e) {
                return value;
            }
        },
        [formatters, secondAxisParameters]
    );

    const makeTooltipFormatter = useMemo(() => {
        return memoize((field) => {
            if (memoizedFormatters[field])
                memoizedFormatters.current.cache.clear();
            const formatter = (value) => {
                try {
                    if (secondAxisParameters.includes(field))
                        return formatters.y2?.formatter(value);
                    return formatters.y1?.formatter(value);
                } catch (e) {
                    return value;
                }
            };
            memoizedFormatters.current[field] = formatter;
            return formatter;
        });
    }, [formatters, secondAxisParameters]);

    const dotRenderer = useCallback(
        (props) => {
            if (!highlight || props?.payload?.name !== highlight.value)
                return null;
            console.log(props);
            const color = getColorByName("info");
            return (
                <circle
                    r={props.r}
                    strokeWidth={props.strokeWidth}
                    key={props.key}
                    cx={props.cx}
                    cy={props.cy}
                    fill={color}
                    width={props.width}
                    height={props.height}
                    stroke={color}
                    fillOpacity={props.fillOpacity}
                />
            );
        },
        [highlight]
    );

    const {position, angle} = useMemo(
        () => parseLabelList(labelList),
        [labelList]
    );

    const {height, xAxisHeight} = usePrintHeight(print, data, formatters);

    const xAxisDataKey = useMemo(() => {
        if (data[0] && typeof data[0].name === "object") return "name.name";
        return "name";
    }, [data]);

    return (
        <div className="fetura-chart area-chart">
            <ResponsiveContainer
                width={"100%"}
                height={height}
                className="fetura-chart-container">
                <AreaChart
                    data={data}
                    onClick={onClick}
                    margin={
                        showAxisName
                            ? {top: 0, right: 0, bottom: 20, left: 0}
                            : {top: 0, right: 0, bottom: 0, left: 0}
                    }>
                    <XAxis
                        dataKey={xAxisDataKey}
                        label={
                            showAxisName
                                ? {
                                      position: "bottom",
                                      value: label,
                                      offset: !!print ? 50 : 0,
                                  }
                                : null
                        }
                        tickFormatter={formatters.x?.formatter}
                        height={xAxisHeight}
                        angle={print ? 315 : undefined}
                        textAnchor={print ? "end" : "middle"}
                    />
                    <Tooltip
                        formatter={tooltipFormatter}
                        labelFormatter={formatters.x?.formatter}
                        content={
                            <CustomTooltip
                                xAxisName={label}
                                showOnlyLabelsWithValue={
                                    showOnlyLabelsWithValue
                                }
                            />
                        }
                    />
                    {renderLegend(onLegendClick, legend)}
                    {renderCartesianGrid(print)}
                    {fields.map((field, index) => (
                        <Area
                            key={field}
                            dataKey={field}
                            stroke={chartColors[field]}
                            fill={chartColors[field]}
                            fillOpacity={0.4}
                            strokeWidth={4}
                            stackId={fields.length > 1 ? 1 : null}
                            yAxisId={
                                secondAxisParameters.includes(field)
                                    ? "right"
                                    : "left"
                            }
                            hide={disabled.includes(field)}
                            dot={dotRenderer}
                            isAnimationActive={!print}
                            name={createName(
                                field,
                                sum[field],
                                translatedGroupNames,
                                translatedIDs
                            )}>
                            {labelList && labelList !== "disabled" && (
                                <LabelList
                                    dataKey={field}
                                    position={position}
                                    angle={angle}
                                    formatter={makeTooltipFormatter(field)}
                                    style={{
                                        textAnchor:
                                            labelList === "insideTop#270"
                                                ? "end"
                                                : angle === 270
                                                  ? "start"
                                                  : "middle", // textAnchor prop nie dziala
                                        dominantBaseline:
                                            angle === 270 &&
                                            labelList !== "insideTop#270"
                                                ? "middle"
                                                : "auto",
                                    }}
                                />
                            )}
                        </Area>
                    ))}
                    <YAxis
                        yAxisId="left"
                        tickFormatter={formatters.y1?.formatter}
                        includeHidden
                        domain={leftDomain}
                    />
                    {secondAxisParameters.length > 0 && (
                        <YAxis
                            yAxisId="right"
                            orientation="right"
                            tickFormatter={formatters.y2?.formatter}
                            includeHidden
                            domain={rightDomain}
                        />
                    )}
                    {referenceAreasRenderer(referenceLines)}
                </AreaChart>
            </ResponsiveContainer>
        </div>
    );
}

function SQLBarChart({
    data,
    fields,
    chartColors,
    label,
    referenceLines,
    secondAxisParameters,
    formatters,
    onClick,
    highlight,
    stack,
    legend,
    showAxisName,
    leftDomain,
    rightDomain,
    print,
    sum,
    translatedGroupNames,
    translatedIDs,
    labelList,
    showOnlyLabelsWithValue,
}) {
    const container = useRef();

    const [disabled, onLegendClick] = useLegendClick();
    const memoizedFormatters = useRef({});

    const tooltipFormatter = useCallback(
        (value, _, props) => {
            try {
                if (formatters[props.dataKey])
                    return formatters[props.dataKey].formatter(value);
                if (secondAxisParameters.includes(props.dataKey))
                    return formatters.y2?.formatter(value);
                return formatters.y1?.formatter(value);
            } catch (e) {
                return value;
            }
        },
        [formatters, secondAxisParameters]
    );

    const makeTooltipFormatter = useMemo(() => {
        return memoize((field) => {
            if (memoizedFormatters[field])
                memoizedFormatters.current.cache.clear();
            const formatter = (value) => {
                try {
                    if (secondAxisParameters.includes(field))
                        return formatters.y2?.formatter(value);
                    return formatters.y1?.formatter(value);
                } catch (e) {
                    return value;
                }
            };
            memoizedFormatters.current[field] = formatter;
            return formatter;
        });
    }, [formatters, secondAxisParameters]);

    const getBackground = useCallback(
        (props) => {
            if (!highlight || props?.payload?.name !== highlight.value)
                return null;
            return (
                <path
                    x={props.x}
                    y={props.y}
                    width={props.width}
                    height={props.height}
                    fill={getColorByName("info")}
                    fillOpacity={0.3}
                    className="recharts-rectangle recharts-bar-background-rectangle"
                    d={`M ${props.x},${props.y} h ${props.width} v ${props.height} h -${props.width} Z`}
                />
            );
        },
        [highlight]
    );

    const {height, xAxisHeight} = usePrintHeight(print, data, formatters);

    const {position, angle} = useMemo(
        () => parseLabelList(labelList),
        [labelList]
    );

    const labelListOffset = useMemo(() => {
        if (!labelList || position !== "top") return undefined;
        if (angle !== 270) return {top: 15};
        let longestString = 0;
        for (let item of data) {
            for (let field of fields) {
                const tmp =
                    typeof item[field] === "string"
                        ? item[field]
                        : String(item[field]);
                if (tmp.length > longestString) longestString = tmp.length;
            }
        }
        return {top: longestString * 6 + 5};
    }, [labelList, position, angle, data, fields]);

    const chartWidth = useChartWidth(container);

    const xAxisDataKey = useMemo(() => {
        if (data[0] && typeof data[0].name === "object") return "name.name";
        return "name";
    }, [data]);

    return (
        <div className="fetura-chart bar-chart" ref={container}>
            <ResponsiveContainer
                width={chartWidth}
                height={height}
                className="fetura-chart-container">
                <BarChart
                    data={data}
                    margin={
                        showAxisName
                            ? {top: 0, right: 0, bottom: 20, left: 0}
                            : {top: 0, right: 0, bottom: 0, left: 0}
                    }>
                    <XAxis
                        dataKey={xAxisDataKey}
                        label={
                            showAxisName
                                ? {
                                      position: "bottom",
                                      value: label,
                                      offset: !!print ? 50 : 0,
                                  }
                                : null
                        }
                        tickFormatter={formatters.x?.formatter}
                        angle={print ? 315 : undefined}
                        // angle={315}
                        // textAnchor={"end"}
                        textAnchor={print ? "end" : "middle"}
                        height={xAxisHeight}
                    />
                    <Tooltip
                        formatter={tooltipFormatter}
                        labelFormatter={formatters.x?.formatter}
                        content={
                            <CustomTooltip
                                xAxisName={label}
                                showOnlyLabelsWithValue={
                                    showOnlyLabelsWithValue
                                }
                            />
                        }
                    />
                    {renderLegend(onLegendClick, legend)}
                    {renderCartesianGrid(print)}
                    {fields.map((field, index) => (
                        <Bar
                            key={field}
                            onClick={onClick}
                            dataKey={field}
                            stroke={chartColors[field]}
                            fill={chartColors[field]}
                            yAxisId={
                                secondAxisParameters.includes(field)
                                    ? "right"
                                    : "left"
                            }
                            hide={disabled.includes(field)}
                            background={getBackground}
                            stackId={stack ? "stack" : null}
                            isAnimationActive={!print}
                            name={createName(
                                field,
                                sum[field],
                                translatedGroupNames,
                                translatedIDs
                            )}>
                            {((labelList && labelList !== "disabled") ||
                                !!print) && (
                                <LabelList
                                    dataKey={field}
                                    position={position}
                                    angle={angle}
                                    formatter={makeTooltipFormatter(field)}
                                    style={{
                                        textAnchor:
                                            labelList === "insideTop#270"
                                                ? "end"
                                                : angle === 270
                                                  ? "start"
                                                  : "middle", // textAnchor prop nie dziala
                                        dominantBaseline:
                                            angle === 270 &&
                                            labelList !== "insideTop#270"
                                                ? "middle"
                                                : "auto",
                                    }}
                                />
                            )}
                        </Bar>
                    ))}
                    <YAxis
                        yAxisId="left"
                        tickFormatter={formatters.y1?.formatter}
                        includeHidden
                        allowDataOverflow
                        domain={leftDomain}
                        padding={labelListOffset}
                    />
                    {secondAxisParameters.length > 0 && (
                        <YAxis
                            yAxisId="right"
                            orientation="right"
                            tickFormatter={formatters.y2?.formatter}
                            includeHidden
                            allowDataOverflow
                            domain={rightDomain}
                            padding={labelListOffset}
                        />
                    )}
                    {referenceAreasRenderer(referenceLines)}
                </BarChart>
            </ResponsiveContainer>
        </div>
    );
}

function SQLScatterChart({
    data,
    fields,
    chartColors,
    label,
    referenceLines,
    secondAxisParameters,
    formatters,
    onClick,
    highlight,
    legend,
    showAxisName,
    leftDomain,
    rightDomain,
    print,
    sum,
    translatedGroupNames,
    translatedIDs,
    showOnlyLabelsWithValue,
    labelList,
}) {
    const memoizedFormatters = useRef({});

    const [disabled, onLegendClick] = useLegendClick();

    const tooltipFormatter = useCallback(
        (value, _, props) => {
            try {
                if (secondAxisParameters.includes(props.dataKey))
                    return formatters.y2?.formatter(value);
                return formatters.y1?.formatter(value);
            } catch (e) {
                return value;
            }
        },
        [formatters, secondAxisParameters]
    );

    const makeTooltipFormatter = useMemo(() => {
        return memoize((field) => {
            if (memoizedFormatters[field])
                memoizedFormatters.current.cache.clear();
            const formatter = (value) => {
                try {
                    if (secondAxisParameters.includes(field))
                        return formatters.y2?.formatter(value);
                    return formatters.y1?.formatter(value);
                } catch (e) {
                    return value;
                }
            };
            memoizedFormatters.current[field] = formatter;
            return formatter;
        });
    }, [formatters, secondAxisParameters]);

    const shapeRenderer = useCallback(
        (props) => {
            const isSelected =
                highlight && props?.payload?.name === highlight.value;
            return (
                <path
                    key={props.key}
                    cx={props.cx}
                    cy={props.cy}
                    fill={isSelected ? getColorByName("info") : props.fill}
                    height={props.height}
                    width={props.width}
                    x={props.x}
                    y={props.y}
                    className="recharts-symbols"
                    transform={`translate(${props.cx}, ${props.cy})`}
                    d="M4.514,0A4.514,4.514,0,1,1,-4.514,0A4.514,4.514,0,1,1,4.514,0"
                />
            );
        },
        [highlight]
    );

    const {height, xAxisHeight} = usePrintHeight(print, data, formatters);

    const splitData = useMemo(() => {
        const tmp = {};
        for (let field of fields) tmp[field] = [];
        for (let row of data) {
            for (let field of fields) {
                if (row[field] !== null) {
                    tmp[field].push({
                        x: row.name?.name || row.name,
                        y: row[field],
                        field,
                    });
                }
            }
        }
        return tmp;
    }, [data, fields]);

    const names = useMemo(() => {
        const tmp = {};
        for (let field of fields) {
            tmp[field] = createName(
                field,
                sum[field],
                translatedGroupNames,
                translatedIDs
            );
        }
        return tmp;
    }, [fields, sum, translatedGroupNames, translatedIDs]);

    const {position, angle} = useMemo(
        () => parseLabelList(labelList),
        [labelList]
    );

    return (
        <div className="fetura-chart scatter-chart">
            <ResponsiveContainer
                width={"100%"}
                height={height}
                className="fetura-chart-container">
                <ScatterChart
                    margin={
                        showAxisName
                            ? {top: 0, right: 0, bottom: 20, left: 0}
                            : {top: 0, right: 0, bottom: 0, left: 0}
                    }>
                    <XAxis
                        dataKey="x"
                        label={
                            showAxisName
                                ? {
                                      position: "bottom",
                                      value: label,
                                      offset: !!print ? 50 : 0,
                                  }
                                : null
                        }
                        tickFormatter={formatters.x?.formatter}
                        height={xAxisHeight}
                        angle={print ? 315 : undefined}
                        textAnchor={print ? "end" : "middle"}
                        allowDuplicatedCategory={false}
                        type="number"
                    />
                    <Tooltip
                        formatter={tooltipFormatter}
                        labelFormatter={formatters.x?.formatter}
                        content={
                            <CustomScatterTooltip
                                xAxisName={label}
                                names={names}
                                chartColors={chartColors}
                                showOnlyLabelsWithValue={
                                    showOnlyLabelsWithValue
                                }
                            />
                        }
                    />
                    {renderLegend(onLegendClick, legend)}
                    {renderCartesianGrid(print, true)}
                    {fields.map((field, index) => (
                        <Scatter
                            key={field}
                            dataKey={field}
                            name={names[field]}
                            data={splitData[field]}
                            fill={chartColors[field]}
                            yAxisId={
                                secondAxisParameters.includes(field)
                                    ? "right"
                                    : "left"
                            }
                            hide={disabled.includes(field)}
                            onClick={onClick}
                            shape={shapeRenderer}
                            isAnimationActive={!print}>
                            {((labelList && labelList !== "disabled") ||
                                !!print) && (
                                <LabelList
                                    dataKey={field}
                                    position={position}
                                    angle={angle}
                                    formatter={makeTooltipFormatter(field)}
                                    style={{
                                        textAnchor:
                                            labelList === "insideTop#270"
                                                ? "end"
                                                : angle === 270
                                                  ? "start"
                                                  : "middle", // textAnchor prop nie dziala
                                        dominantBaseline:
                                            angle === 270 &&
                                            labelList !== "insideTop#270"
                                                ? "middle"
                                                : "auto",
                                    }}
                                />
                            )}
                        </Scatter>
                    ))}
                    <YAxis
                        yAxisId="left"
                        tickFormatter={formatters.y1?.formatter}
                        includeHidden
                        domain={leftDomain}
                        dataKey={"y"}
                        type="number"
                    />
                    {secondAxisParameters.length > 0 && (
                        <YAxis
                            yAxisId="right"
                            orientation="right"
                            tickFormatter={formatters.y2?.formatter}
                            includeHidden
                            domain={rightDomain}
                        />
                    )}
                    {referenceAreasRenderer(referenceLines)}
                </ScatterChart>
            </ResponsiveContainer>
        </div>
    );
}

function SQLPieChart({
    data,
    fields,
    chartColors,
    formatters,
    onClick,
    highlight,
    print,
    sum,
    translatedGroupNames,
    translatedIDs,
    legend,
}) {
    const field = useMemo(() => {
        return fields[0];
    }, [fields]);

    const activeShapeRenderer = useCallback((props) => {
        const RADIAN = Math.PI / 180;
        const {
            cx,
            cy,
            innerRadius,
            outerRadius,
            startAngle,
            endAngle,
            midAngle,
            fill,
        } = props;
        const sin = Math.sin(-RADIAN * midAngle);
        const cos = Math.cos(-RADIAN * midAngle);
        const sx = cx + (outerRadius - 60) * cos;
        const sy = cy + (outerRadius - 60) * sin;
        return (
            <Sector
                cx={sx}
                cy={sy}
                innerRadius={innerRadius}
                outerRadius={outerRadius}
                startAngle={startAngle}
                endAngle={endAngle}
                fill={fill}
            />
        );
    }, []);

    const getPercent = useCallback(
        (value) => {
            if (!sum[field] || isNaN(+sum[field])) return null;
            const percent = (value / +sum[field]) * 100;
            return `[${percent.toFixed(2)}%] `;
        },
        [sum, field]
    );

    const _data = useMemo(() => {
        if (!formatters.x?.formatter) return data;
        return data.map((item) => {
            const percent = getPercent(item?.[field]);
            return {
                ...item,
                name: `${percent || ""}${formatters.x?.formatter(item.name)}`,
                itemName: item.name,
            };
        });
    }, [data, formatters, getPercent, field]);

    const activeIndex = useMemo(() => {
        if (!highlight) return null;
        return _data.findIndex((item) => item.name === highlight.value);
    }, [highlight, _data]);

    return (
        <div
            className={`sql-chart-pie${
                legend !== "none" ? " with-legend" : ""
            }`}
            key={field}>
            <div>
                {createName(
                    field,
                    sum[field],
                    translatedGroupNames,
                    translatedIDs
                )}
            </div>
            <div>
                <div className="fetura-chart">
                    <ResponsiveContainer
                        width={"100%"}
                        height={print ? 400 : "85%"}
                        className="fetura-chart-container">
                        <PieChart
                            margin={{top: 0, right: 0, bottom: 0, left: 0}}>
                            <Tooltip
                                formatter={formatters.y1?.formatter}
                                content={<CustomPieChartTooltip />}
                            />
                            <Pie
                                key={field}
                                data={_data}
                                dataKey={field}
                                nameKey={"name"}
                                cx="50%"
                                cy="50%"
                                onClick={onClick}
                                activeIndex={activeIndex}
                                activeShape={activeShapeRenderer}
                                isAnimationActive={!print}
                                label={!!print}>
                                {_data.map((entry, index) => (
                                    <Cell
                                        key={`cell-${index}`}
                                        fill={
                                            chartColors[field][
                                                index %
                                                    chartColors[field].length
                                            ]
                                        }
                                    />
                                ))}
                            </Pie>
                        </PieChart>
                    </ResponsiveContainer>
                </div>
                {legend !== "none" && (
                    <ul>
                        {_data.map((row, index) => (
                            <li key={index}>
                                <span
                                    className="sql-chart-pie-legend-color"
                                    style={{
                                        background:
                                            chartColors[field][
                                                index %
                                                    chartColors[field].length
                                            ],
                                    }}
                                />
                                <span>{`${row.name} (${row?.[field]})`}</span>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
}

function SQLRadarChart({
    data,
    fields,
    chartColors,
    secondAxisParameters,
    formatters,
    onClick,
    highlight,
    legend,
    print,
    sum,
    labelList,
    translatedGroupNames,
    translatedIDs,
    showOnlyLabelsWithValue,
}) {
    const memoizedFormatters = useRef({});

    const [disabled, onLegendClick] = useLegendClick();

    const tooltipFormatter = useCallback(
        (value, _, props) => {
            try {
                if (secondAxisParameters.includes(props.dataKey))
                    return formatters.y2?.formatter(value);
                return formatters.y1?.formatter(value);
            } catch (e) {
                return value;
            }
        },
        [formatters, secondAxisParameters]
    );

    const makeTooltipFormatter = useMemo(() => {
        return memoize((field) => {
            if (memoizedFormatters[field])
                memoizedFormatters.current.cache.clear();
            const formatter = (value) => {
                try {
                    if (secondAxisParameters.includes(field))
                        return formatters.y2?.formatter(value);
                    return formatters.y1?.formatter(value);
                } catch (e) {
                    return value;
                }
            };
            memoizedFormatters.current[field] = formatter;
            return formatter;
        });
    }, [formatters, secondAxisParameters]);

    const dotRenderer = useCallback(
        (props) => {
            if (!highlight || props?.payload?.name !== highlight.value)
                return null;
            const color = getColorByName("info");
            return (
                <circle
                    r={props.r}
                    strokeWidth={props.strokeWidth}
                    key={props.key}
                    cx={props.cx}
                    cy={props.cy}
                    fill={color}
                    width={props.width}
                    height={props.height}
                    stroke={color}
                    fillOpacity={props.fillOpacity}
                />
            );
        },
        [highlight]
    );

    const {position, angle} = useMemo(
        () => parseLabelList(labelList),
        [labelList]
    );

    return (
        <div className="fetura-chart radar-chart">
            <ResponsiveContainer
                width={"100%"}
                height={!!print ? 600 : "100%"}
                className="fetura-chart-container">
                <RadarChart
                    data={data}
                    onClick={onClick}
                    margin={{top: 0, right: 0, bottom: 0, left: 0}}>
                    <Tooltip
                        formatter={tooltipFormatter}
                        labelFormatter={formatters.x?.formatter}
                        content={
                            <CustomTooltip
                                // xAxisName={label}
                                showOnlyLabelsWithValue={
                                    showOnlyLabelsWithValue
                                }
                            />
                        }
                    />
                    <PolarGrid />
                    <PolarAngleAxis
                        dataKey="name"
                        tickFormatter={formatters.x?.formatter}
                    />
                    {!!print && <PolarRadiusAxis />}
                    {renderLegend(onLegendClick, legend)}
                    {fields.map((field, index) => (
                        <Radar
                            name={createName(
                                field,
                                sum[field],
                                translatedGroupNames,
                                translatedIDs
                            )}
                            dataKey={field}
                            stroke={chartColors[field]}
                            fill={chartColors[field]}
                            fillOpacity={0.4}
                            hide={disabled.includes(field)}
                            dot={dotRenderer}
                            isAnimationActive={!print}>
                            {(labelList && labelList !== "disabled") ||
                                (!!print && (
                                    <LabelList
                                        dataKey={field}
                                        position={position}
                                        angle={angle}
                                        formatter={makeTooltipFormatter(field)}
                                        style={{
                                            textAnchor:
                                                labelList === "insideTop#270"
                                                    ? "end"
                                                    : angle === 270
                                                      ? "start"
                                                      : "middle", // textAnchor prop nie dziala
                                            dominantBaseline:
                                                angle === 270 &&
                                                labelList !== "insideTop#270"
                                                    ? "middle"
                                                    : "auto",
                                        }}
                                    />
                                ))}
                        </Radar>
                    ))}
                </RadarChart>
            </ResponsiveContainer>
        </div>
    );
}

// function SQLTable({
//     data,
//     fields,
//     label,
//     formatters,
//     onClick,
//     highlight,
//     frozenHeaders,
//     translatedIDs,
//     showIndex,
//     print,
//     summaryFields,
//     translatedGroupNames,
//     dataTransposition,
// }) {
//     const getSortFormatter = useSQLTableSortFormatter();
//     const getFilterType = useSQLTableFilterType();

//     const getField = useCallback(
//         (name, field, formatterField) => {
//             let split = name.split("#");
//             let newName =
//                 split.length === 1 ? split[0] : `[${split[1]}] ${split[0]}`;
//             const frozen = frozenHeaders.includes(field);
//             if (formatters[formatterField]?.type === ParamTypes.ANIMAL) {
//                 return {
//                     name: newName,
//                     field,
//                     component: formatters[formatterField].formatter,
//                     sortFormatter: getSortFormatter(
//                         formatters[formatterField].type
//                     ),
//                     frozen,
//                     filterType: FilterTypes.STRING,
//                 };
//             }
//             return {
//                 name: newName,
//                 field,
//                 valueFormatter: formatters[formatterField]?.formatter,
//                 sortFormatter: getSortFormatter(
//                     formatters[formatterField]?.type
//                 ),
//                 frozen,
//                 filterType: getFilterType(formatters[formatterField]?.type),
//             };
//         },
//         [formatters, getSortFormatter, frozenHeaders, getFilterType]
//     );

//     const headers = useMemo(() => {
//         return [
//             {...getField(label, "name", "x"), frozen: true},
//             ...fields.map((field) =>
//                 getField(
//                     createName(
//                         field,
//                         undefined,
//                         translatedGroupNames,
//                         translatedIDs
//                     ),
//                     field,
//                     field
//                 )
//             ),
//         ];
//     }, [fields, label, getField, translatedIDs, translatedGroupNames]);

//     // const getCellClassName = useCallback(
//     //     (props) => {
//     //         if (props.name === highlight?.value) return "selected";
//     //         return null;
//     //     },
//     //     [highlight]
//     // );

//     // const getCache = useMemo(
//     //     () => memoizeOne((d) => ({})),
//     //     [data, headers, summaryFields] // eslint-disable-line react-hooks/exhaustive-deps
//     // );

//     // const translateType = useCallback((type) => {
//     //     switch (type) {
//     //         case AggrTypes.MOST_COMMON:
//     //             return "Naj. wyst. wartość";
//     //         case AggrTypes.AVERAGE:
//     //             return "Średnia";
//     //         case AggrTypes.COUNT_DISTINCT:
//     //             return "Unikatowe wartości";
//     //         case AggrTypes.SUM:
//     //             return "Suma";
//     //         case AggrTypes.WEIGHTED_AVERAGE:
//     //             return "Średnia ważona";
//     //         case AggrTypes.MAX:
//     //             return "Maks. wartość";
//     //         case AggrTypes.MIN:
//     //             return "Min. wartość";
//     //         default:
//     //             return type;
//     //     }
//     // }, []);

//     // const bottomPinnedRows = useMemo(() => {
//     //     if (summaryFields.length === 0) return null;
//     //     return function (
//     //         pageData,
//     //         _unusedPaginationItems,
//     //         _unusedRawData,
//     //         filteredData
//     //     ) {
//     //         let tmp = [{}];
//     //         const cache = getCache(filteredData);
//     //         for (let i = 0; i < summaryFields.length; i++) {
//     //             const field = summaryFields[i];
//     //             const headersWithID = headers.filter((item) =>
//     //                 item.field.includes(field.id)
//     //             );
//     //             for (let header of headersWithID) {
//     //                 const detail = createDetail(
//     //                     field.type,
//     //                     {massKey: `this["${header.field}"]`},
//     //                     header.field,
//     //                     pageData,
//     //                     filteredData,
//     //                     {
//     //                         cache,
//     //                         cacheKey: `${header.field}_${i}`,
//     //                         formatter:
//     //                             field.type === AggrTypes.MOST_COMMON
//     //                                 ? header.valueFormatter
//     //                                 : undefined,
//     //                         onlyGlobal: true,
//     //                     }
//     //                 );
//     //                 tmp[0][header.field] = `${translateType(
//     //                     field.type
//     //                 )}: ${detail}`;
//     //             }
//     //         }
//     //         return tmp;
//     //     };
//     // }, [summaryFields, getCache, headers, translateType]);

//     // if (print) {
//     //     return (
//     //         <SQLPrintTable
//     //             headers={headers}
//     //             data={data}
//     //             showIndex={showIndex}
//     //         />
//     //     );
//     // }
//     // return (
//     //     <SQLTableComponent
//     //         headers={headers}
//     //         data={data}
//     //         getCellClassName={getCellClassName}
//     //         onClick={onClick}
//     //         showIndex={showIndex}
//     //         isVertical={dataTransposition}
//     //         bottomPinnedRows={bottomPinnedRows}
//     //     />
//     // );
//     return <AsyncTable headers={headers} shouldIndex={showIndex} />;
// }

function SQLChartRenderer({
    data,
    chartType = "Area",
    label,
    referenceLines,
    secondAxisParameters,
    formatters,
    onClick,
    highlight,
    stack,
    legend,
    showAxisName,
    leftDomain,
    rightDomain,
    print,
    chartColors,
    translatedGroupNames,
    frozenHeaders,
    translatedIDs,
    showIndex,
    summaryFields,
    legendCalculation,
    labelList,
    showOnlyLabelsWithValue,
    dataTransposition,
}) {
    const {t} = useTranslation();

    console.error("SQLChartRenderer received data:", translatedIDs);

    const fields = useMemo(() => {
        return Object.keys(translatedIDs);
    }, [translatedIDs]);

    const fieldsToRender = useMemo(() => {
        return fields.filter((item) => item !== "name");
    }, [fields]);

    const Component = useMemo(() => {
        switch (chartType) {
            case "Bar":
                return SQLBarChart;
            case "Scatter":
                return SQLScatterChart;
            case "Pie":
                return SQLPieChart;
            case "Radar":
                return SQLRadarChart;
            // case "Table":
            //     return SQLTable;
            default:
                return SQLAreaChart;
        }
    }, [chartType]);

    const sum = useMemo(() => {
        if (chartType === "Table") return {};
        const tmp = {};
        for (let field of fieldsToRender) {
            const calculation = legendCalculation[field];
            if (calculation === "NONE") continue;
            const res = calculateLegendResult(data, field, calculation);
            if (typeof res === "number" && !isNaN(res)) {
                if (formatters[field]) {
                    tmp[field] = formatters[field].formatter(res);
                } else if (formatters.y1 || formatters.y2) {
                    if (formatters.y1?.parameters.includes(field)) {
                        tmp[field] = formatters.y1.formatter(res);
                    } else if (formatters.y2) {
                        tmp[field] = formatters.y2.formatter(res);
                    }
                } else {
                    tmp[field] = res;
                }
            }
        }
        return tmp;
    }, [data, fieldsToRender, formatters, chartType, legendCalculation]);

    return (
        <ErrorBoundary t={t}>
            <Component
                data={data}
                fields={fieldsToRender}
                chartColors={chartColors}
                label={label}
                referenceLines={referenceLines}
                secondAxisParameters={secondAxisParameters}
                formatters={formatters}
                onClick={onClick}
                dataTransposition={dataTransposition}
                highlight={highlight}
                stack={stack}
                legend={legend}
                showAxisName={showAxisName}
                leftDomain={leftDomain}
                rightDomain={rightDomain}
                print={print}
                sum={sum}
                translatedGroupNames={translatedGroupNames}
                frozenHeaders={frozenHeaders}
                translatedIDs={translatedIDs}
                showIndex={showIndex}
                summaryFields={summaryFields}
                labelList={labelList}
                showOnlyLabelsWithValue={showOnlyLabelsWithValue}
            />
        </ErrorBoundary>
    );
}

function SQLChartNoData() {
    const {t} = useTranslation();

    return (
        <div className="sql-chart-empty">
            <div>
                <i className="fa-solid fa-empty-set" />
            </div>
            <div className="fw-bold">{t("notFound.dataNotFound")}</div>
            <small className="opacity-50">{t("tryToModifyQuery")}</small>
        </div>
    );
}

export default function SQLChart({data, ...props}) {
    if (data.length === 0) return <SQLChartNoData />;
    return <SQLChartRenderer {...props} data={data} />;
}
